import { Dispatch } from "redux";

import { loadAndTrack } from "redux-request-loading";
import axios from "axios";

import { Payload } from "@neworbit/redux-helpers";

import { CaseType } from "@teamprevent/common/model";
import { toast } from "@teamprevent/common/toasts";
import HrRepresentativesApi from "@teamprevent/common/questionnaires/components/referralForm/HrRepresentativesApi";
import LineManagersApi from "@teamprevent/common/questionnaires/components/referralForm/LineManagersApi";
import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import { LineManager } from "@teamprevent/common/lineManagers/models";
import { LinkObject } from "@teamprevent/common/model";

import { workAdjustmentApi } from "../../workAdjustment/WorkAdjustmentApi";

import * as actions from "./actiontypes";
import * as models from "./model";
import OhCaseApi from "./ohCaseApi";
import { OhCaseDocumentApi } from "./ohCaseDocumentApi";
import { LoadOhCaseDocumentsPayload, LoadOhCaseAppointmentsPayload } from "./model";
import { ChangeWorkAdjustmentCaseSecondaryStatusModel } from "./components/details/workAdjustment/models";

import { push } from "redux-little-router";

export type OhCaseAction =
      ({ type: typeof actions.LOAD_OHCASES_SUCCESS } & Payload<models.OhCaseListModel[]>)
    | ({ type: typeof actions.CREATE_OHCASE_SUCCESS } & Payload<models.OhCaseDetailModel>)
    | ({ type: typeof actions.LOAD_OHCASE_DETAIL_SUCCESS } & Payload<models.OhCaseDetailModel>)
    | ({ type: typeof actions.SAVE_OHCASE_SUCCESS } & Payload<models.OhCaseDetailModel>)
    | ({ type: typeof actions.CANCEL_OHCASE_SUCCESS } & Payload<any>)
    | ({ type: typeof actions.DELETE_OHCASE_SUCCESS } & Payload<any>)
    | ({ type: typeof actions.LOAD_OHCASE_DOCUMENTS_SUCCESS } & Payload<LoadOhCaseDocumentsPayload>)
    | ({ type: typeof actions.LOAD_OHCASE_APPOINTMENTS_SUCCESS } & Payload<LoadOhCaseAppointmentsPayload>)
    | ({ type: typeof actions.CHANGE_WORK_ADJUSTMENT_CASE_SECONDARY_STATUS_SUCCESS } & Payload<models.OhCaseDetailModel>)
    | ({ type: typeof actions.CREATE_OHCASE_SUCCESS } & Payload<models.OhCaseDetailModel>)
    | ({ type: typeof actions.LOAD_LINE_MANAGERS_SUCCESS } & Payload<{ id: string; lineManagers: LineManager[] }>)
    | ({ type: typeof actions.LOAD_HR_REPRESENTATIVES_SUCCESS } & Payload<{ id: string; hrRepresentatives: HrRepresentative[] }>)
    | ({ type: typeof actions.UPDATE_ADDITIONAL_MANAGERS_SUCCESS } &
        Payload<{ id: string; associatedManagers: LinkObject[]; associatedHrUsers: LinkObject[] }>);

export const loadOhCasesSuccess = (payload: models.OhCaseListModel[]): OhCaseAction => ({
    payload,
    type: actions.LOAD_OHCASES_SUCCESS
});

export const loadOhCaseAppointmentsSuccess = (payload: LoadOhCaseAppointmentsPayload): OhCaseAction => ({
    payload,
    type: actions.LOAD_OHCASE_APPOINTMENTS_SUCCESS
});

export const createOhCaseSuccess = (payload: models.OhCaseDetailModel): OhCaseAction => ({
    payload,
    type: actions.CREATE_OHCASE_SUCCESS
});

export const saveOhCaseSuccess = (payload: models.OhCaseDetailModel): OhCaseAction => ({
    payload,
    type: actions.SAVE_OHCASE_SUCCESS
});

export const deleteOhCaseSuccess = (id: any): OhCaseAction => ({
    payload: id,
    type: actions.DELETE_OHCASE_SUCCESS
});

export const cancelOhCaseSuccess = (id: any): OhCaseAction => ({
    payload: id,
    type: actions.CANCEL_OHCASE_SUCCESS
});

export const loadOhCaseDocumentsSuccess = (payload: LoadOhCaseDocumentsPayload): OhCaseAction => ({
    payload,
    type: actions.LOAD_OHCASE_DOCUMENTS_SUCCESS
});

export const changeWorkAdjustmentCaseSecondaryStatusSuccess = (payload: models.OhCaseDetailModel): OhCaseAction => ({
    payload,
    type: actions.CHANGE_WORK_ADJUSTMENT_CASE_SECONDARY_STATUS_SUCCESS
});

export const loadLineManagersSuccess = (payload: { id: string; lineManagers: LineManager[] }): OhCaseAction => ({
    payload,
    type: actions.LOAD_LINE_MANAGERS_SUCCESS
});

export const loadHrRepresentativesSuccess = (payload: { id: string; hrRepresentatives: HrRepresentative[] }): OhCaseAction => ({
    payload,
    type: actions.LOAD_HR_REPRESENTATIVES_SUCCESS
});

export const updateAdditionalManagersSuccess = (
    payload:
        { id: string; associatedManagers: LinkObject[]; associatedHrUsers: LinkObject[] }): OhCaseAction => ({
    payload,
    type: actions.UPDATE_ADDITIONAL_MANAGERS_SUCCESS
});

export const buildLoadOhCaseDocuments = (ohCaseDocumentApi: OhCaseDocumentApi) =>
    ({ ohCaseId }: { ohCaseId: string }) => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(dispatch, actions.LOAD_OHCASE_DOCUMENTS, ohCaseDocumentApi.getAll(ohCaseId));
            dispatch(loadOhCaseDocumentsSuccess({ ohCaseId, documents: result }));
        };
    };

export const loadOhCaseDocuments = buildLoadOhCaseDocuments(new OhCaseDocumentApi(axios));

export function createOhCase(ohCase: models.OhCaseCreateModel, path: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.CREATE_OHCASE, OhCaseApi.create(ohCase));
        dispatch(createOhCaseSuccess(result));
        dispatch(push(`${path}/${result.id}`, {}));
    };
}

export function saveOhCase(ohCase: models.OhCaseEditModel, path?: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.SAVE_OHCASE, OhCaseApi.save(ohCase));

        dispatch(saveOhCaseSuccess(result));
        if (path !== undefined) {
            dispatch(push(`${path}/${result.id}`, {}));
        }
    };
}

export function deleteOhCase(id: any, path: string) {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(dispatch, "deleteOhCase", OhCaseApi.delete(id));
        dispatch(deleteOhCaseSuccess(id));
        dispatch(push(`${path}`, {}));
    };
}

export function cancelOhCase(id: string, notes: string, path: string) {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(dispatch, "cancelOhCase", OhCaseApi.cancel(id, notes));
        dispatch(cancelOhCaseSuccess(id));
        dispatch(push(`${path}`, {}));
    };
}

export function changeWorkAdjustmentCaseSecondaryStatus(workAdjustmentCaseId: string, model: ChangeWorkAdjustmentCaseSecondaryStatusModel) {
    return async (dispatch: Dispatch) => {
        const workAdjustmentCase: models.OhCaseDetailModel = await loadAndTrack(
            dispatch,
            actions.CHANGE_WORK_ADJUSTMENT_CASE_SECONDARY_STATUS,
            workAdjustmentApi.changeWorkAdjustmentCaseSecondaryStatus(workAdjustmentCaseId, model)
        );
        dispatch(changeWorkAdjustmentCaseSecondaryStatusSuccess(workAdjustmentCase));
    };
}

export function loadOhCases({ status, caseId, employeeFirstName, employeeLastName, caseType, include }: {
    status: string;
    caseId: string;
    employeeFirstName: string;
    employeeLastName: string;
    caseType?: CaseType;
    include?: CaseType;
}) {
    return async (dispatch: Dispatch) => {
        await loadOhCasesForCaseType(dispatch, status, caseId, employeeFirstName, employeeLastName, `${include ?? ""}${caseType ?? ""}`);
    };
}

export function loadWorkAdjustments({ status, caseId, employeeFirstName, employeeLastName, secondaryStatus }: {
    status: string;
    caseId: string;
    employeeFirstName: string;
    employeeLastName: string;
    secondaryStatus: string;
}) {
    return async (dispatch: Dispatch) => {
        await loadOhCasesForCaseType(dispatch, status, caseId, employeeFirstName, employeeLastName, CaseType.WORK_ADJUSTMENT, secondaryStatus);
    };
}

const loadOhCasesForCaseType = async (
    dispatch: Dispatch, status: string, caseId: string, employeeFirstName: string, employeeLastName: string, include?: string, secondaryStatus?: string) => {

    let statusQueryParam = null;
    if (!status) {
        statusQueryParam = [models.OPEN_CASE_STATUS, models.CANCELLED_CASE_STATUS, models.CLOSED_CASE_STATUS, models.RECALL_CASE_STATUS].join(";");
    } else if (status !== models.ALL_CASE_STATUSES) {
        const statusList = status.split(";");

        if (statusList.some(s => s === models.ALL_CASE_STATUSES)) {
            statusQueryParam = statusList.filter(s => s !== models.ALL_CASE_STATUSES).join(";");
        }
        else {
            statusQueryParam = status;
        }
    }

    const result = await loadAndTrack(dispatch, "loadOhCases", OhCaseApi.getAll({
        status: statusQueryParam,
        caseId,
        employeeFirstName,
        employeeLastName,
        include,
        secondaryStatus
    }));
    dispatch(loadOhCasesSuccess(result));
};

export const loadOhCaseDetailSuccess = (ohCase: models.OhCaseDetailModel) => ({
    payload: ohCase,
    type: actions.LOAD_OHCASE_DETAIL_SUCCESS
});

export function loadOhCaseDetail({ ohCaseId, forceLoad }: { ohCaseId: string; forceLoad?: boolean  }) {
    return async (dispatch: Dispatch, getState: () => models.AppState) => {
        const ohCase = getState().ohCases.filter(c => c.id === ohCaseId)[0];

        if (ohCase === undefined
            || ohCase.detailUpdated === undefined
            || ohCase.detailUpdated.isBefore(ohCase.listUpdated)
            || forceLoad) {

            const result = await loadAndTrack(dispatch, "loadOhCaseDetail", OhCaseApi.detail(ohCaseId));
            dispatch(loadOhCaseDetailSuccess(result));
        }
    };
}

export function loadOhCaseAppointments({ ohCaseId }: { ohCaseId: string }) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "loadOhCaseAppointments", OhCaseApi.getAppointments(ohCaseId));
        dispatch(loadOhCaseAppointmentsSuccess({ ohCaseId, appointments: result }));
    };
}

export function loadLineManagerValues({ ohCaseId }: { ohCaseId: string }) {
    return async (dispatch: Dispatch) => {
        const loadLineManagers = loadAndTrack(dispatch, "loadLineManagers", LineManagersApi.getAll());
        const lineManagers = await loadLineManagers;
        return dispatch(loadLineManagersSuccess({ id: ohCaseId, lineManagers }));
    };
}

export function loadHrRepresentativeValues({ ohCaseId }: { ohCaseId: string }) {
    return async (dispatch: Dispatch) => {
        const loadHrRepresentatives = loadAndTrack(dispatch, "loadHrRepresentatives", HrRepresentativesApi.getAll());
        const hrRepresentatives = await loadHrRepresentatives;
        return dispatch(loadHrRepresentativesSuccess({ id: ohCaseId, hrRepresentatives }));
    };
}

export function updateAdditionalManagers(
    ohCaseId: string,
    associatedManagers: LinkObject[],
    associatedHrUsers: LinkObject[],
    path?: string,
    currentUserId?: string,
    isBbvOrImmsCase?: boolean) {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(
            dispatch,
            actions.UPDATE_ADDITIONAL_MANAGERS,
            OhCaseApi.updateAssociatedManagers(ohCaseId, associatedManagers, associatedHrUsers));
        toast.success("Additional managers updated successfully");

        dispatch(updateAdditionalManagersSuccess({ id: ohCaseId, associatedManagers, associatedHrUsers }));

        if (path !== undefined) {
            if (!isBbvOrImmsCase ||
                associatedManagers.some(m => m.id === currentUserId) ||
                associatedHrUsers.some(m => m.id === currentUserId)) {
                dispatch(push(`${path}/${ohCaseId}`, {}));
            }
            else {
                dispatch(push(path));
            }
        }
    };
}
